import { motion, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { PHOTOS } from "@/lib/photos";
import lotMapBase from "@/assets/lot-map-roostertail.png";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
import { ExternalLink } from "lucide-react";
import CaptainsWelcomeBand from "@/components/CaptainsWelcomeBand";

// ── Locations ───────────────────────────────────────────────
const ROOSTERTAIL_ADDRESS = "100 Marquette Dr, Detroit, MI 48214";
const RIVERSIDE_ADDRESS = "Riverside Marina, 11000 Freud St, Detroit, MI 48214";
const RIVERSIDE_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  RIVERSIDE_ADDRESS
)}`;

const noiseBg = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
};

// ── Quick FAQ ───────────────────────────────────────────────
const PARKING_FAQ = [
  {
    q: "How much is parking?",
    a: "It depends on the day. Friday (July 17): $20 per vehicle at Riverside Marina. Saturday (July 18): FREE until 2 PM at Riverside Marina, then $20. Either day you pay at the lot when you arrive — cash only, no reservation needed. Free parking is only at Riverside Marina, and only on Saturday. Lots on the Roostertail property are first come, first served, $30 to $100 per day depending on the lot.",
  },
  {
    q: "Is parking free on Friday?",
    a: "No — Friday parking at Riverside Marina is $20, cash only. The free-until-2-PM window is Saturday only, when doors open at noon (Friday doors open at 3 PM). The shuttle and ferry are free both days no matter what you paid to park.",
  },
  {
    q: "How do I get from Riverside Marina to the festival?",
    a: "Two ways, both free with your ticket: a 5–6 minute shuttle ride straight to the festival doors (two shuttles running constantly, both days), or an 8-minute ferry ride down the Detroit River right to the High Noon deck. Check in at the Yacht Rock Detroit tent when you pull in; bathrooms are on site.",
  },
  {
    q: "Can I leave and come back to my parking spot?",
    a: "No in-and-out privileges at any lot. You can arrive whenever you like, but once you're parked, you're parked for the day.",
  },
  {
    q: "Can I reserve a parking spot at the Roostertail?",
    a: "No — the Roostertail lots are first come, first served: Premium (Main Lot) $100, Medium Lot $40, Back Lot $30 per day, and you pay when you arrive. We can't guarantee a spot, but there should still be spots available both days. If the lots are full, park at Riverside Marina — the shuttle and ferry to the doors are free.",
  },
  {
    q: "Should I just take an Uber?",
    a: "Honestly? We recommend it. Rideshare drop-off is right at the Roostertail entrance (100 Marquette Dr) — no parking, no shuttle, no waiting after the show ends.",
  },
];

const FaqRow = ({
  q,
  a,
  isOpen,
  onToggle,
}: {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <div className="border-b border-yacht-charcoal/10 last:border-b-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 py-5 text-left cursor-pointer"
      aria-expanded={isOpen}
    >
      <span className="text-yacht-charcoal font-bold text-base md:text-lg leading-snug">
        {q}
      </span>
      <motion.span
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.2 }}
        className="text-2xl font-light leading-none text-yacht-blue flex-shrink-0"
      >
        +
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="overflow-hidden"
        >
          <p className="pb-5 text-yacht-charcoal/65 text-sm md:text-base leading-relaxed max-w-2xl">
            {a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/**
 * Labeled lot map — OSM base image with branded pins for each lot.
 * Pin positions are percentages of the image (verified against Teddy's
 * marked-up screenshot: bottom strip = Premium, Sindbad's rows = Medium,
 * Freud St lot = Back).
 */
const LOT_MAP_PINS = [
  { label: "Back Lot", x: 30.0, y: 13.9, accent: false },
  { label: "Medium Lot", x: 47.0, y: 28.6, accent: false },
  { label: "Premium — Main Lot", x: 40.0, y: 55.4, accent: true },
  { label: "The Roostertail", x: 50.5, y: 79.0, accent: false, venue: true },
];

const LotMap = () => (
  <motion.div
    className="bg-white rounded-2xl overflow-hidden shadow-lg border border-yacht-charcoal/10"
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5 }}
  >
    <div className="relative">
      <img
        src={lotMapBase}
        alt="Map of the Roostertail parking lots: Back Lot near Freud Street, Medium Lot by Sindbad's, and the Premium Main Lot on Marquette Drive closest to the venue"
        className="w-full h-auto block"
        loading="lazy"
        decoding="async"
      />
      {LOT_MAP_PINS.map((pin) => (
        <div
          key={pin.label}
          className="absolute -translate-x-1/2 -translate-y-full flex flex-col items-center"
          style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
        >
          <span
            className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] md:text-xs font-bold shadow-md ${
              pin.venue
                ? "bg-yacht-blue text-white"
                : pin.accent
                  ? "bg-yacht-yellow text-yacht-charcoal"
                  : "bg-yacht-charcoal text-white"
            }`}
          >
            {pin.label}
          </span>
          <span
            className={`w-0 h-0 border-l-[6px] border-r-[6px] border-t-[7px] border-l-transparent border-r-transparent ${
              pin.venue
                ? "border-t-yacht-blue"
                : pin.accent
                  ? "border-t-yacht-yellow"
                  : "border-t-yacht-charcoal"
            }`}
          />
        </div>
      ))}
      <span className="absolute bottom-1 right-2 text-[9px] text-yacht-charcoal/50 bg-white/70 px-1 rounded">
        Map © OpenStreetMap contributors
      </span>
    </div>
    <div className="px-5 md:px-6 py-4">
      <p className="text-yacht-charcoal/70 text-sm leading-relaxed">
        All three lots are on the Roostertail property — enter from E Jefferson
        Ave at Marquette Dr and staff will direct you to the next open lot.
      </p>
    </div>
  </motion.div>
);

const Parking = () => {
  usePageSEO({
    title: "Parking — Yacht Rock Detroit 2026 | Roostertail & Riverside Marina",
    description:
      "Parking for Yacht Rock Detroit 2026 at The Roostertail, July 17–18. Park at Riverside Marina: $20 Friday, FREE until 2 PM Saturday (then $20, cash only), with free shuttle & ferry both days — or take an Uber. Roostertail lots are first come, first served.",
    keywords:
      "yacht rock detroit parking, roostertail parking, riverside marina parking detroit, detroit festival parking, roostertail shuttle, detroit river ferry festival",
    ogImage: PHOTOS.CROWD.sunsetStageView,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Parking", url: "https://yachtrockdetroit.com/parking" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative bg-yacht-blue overflow-hidden pt-28 md:pt-40 pb-12 md:pb-16"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={noiseBg}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-8 text-center">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            Getting There · July 17–18
          </motion.p>
          <div
            className="text-[10vw] md:text-7xl font-bold text-white leading-[0.95] tracking-tight"
            role="heading"
            aria-level={1}
          >
            <GradualSpacing text="Parking" duration={0.5} delayMultiple={0.04} />
          </div>
        </div>
      </section>

      {/* FREE UNTIL 2 PM SATURDAY — the headline of the whole page */}
      <section className="relative bg-yacht-yellow overflow-hidden py-10 md:py-14 px-6 md:px-8">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={noiseBg}
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-yacht-charcoal uppercase tracking-tight leading-[0.95]">
            Free parking
            <br />
            Saturday until 2 PM
          </h2>
          <div className="mt-6 md:mt-8 max-w-2xl mx-auto grid grid-cols-2 divide-x divide-yacht-charcoal/20 text-left">
            <div className="pr-5 md:pr-8">
              <p className="text-yacht-charcoal/60 text-[11px] md:text-sm font-semibold tracking-[0.2em] uppercase">
                Friday · July 17
              </p>
              <p className="text-yacht-charcoal font-bold text-xl md:text-3xl mt-1">
                $20 all day
              </p>
              <p className="text-yacht-charcoal/70 text-sm md:text-base mt-1">
                No free window Friday — doors open at 3 PM.
              </p>
            </div>
            <div className="pl-5 md:pl-8">
              <p className="text-yacht-charcoal/60 text-[11px] md:text-sm font-semibold tracking-[0.2em] uppercase">
                Saturday · July 18
              </p>
              <p className="text-yacht-charcoal font-bold text-xl md:text-3xl mt-1">
                FREE until 2 PM
              </p>
              <p className="text-yacht-charcoal/70 text-sm md:text-base mt-1">
                $20 after 2 PM — doors open at noon.
              </p>
            </div>
          </div>
          <p className="text-yacht-charcoal/80 font-semibold text-base md:text-xl mt-6 md:mt-8">
            Riverside Marina only — cash only.
          </p>
        </div>
      </section>

      <CaptainsWelcomeBand />

      {/* YOUR THREE OPTIONS — editorial index, no cards */}
      <section className="py-16 md:py-24 px-6 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.p
            className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-8 md:mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            The Plan: Park at Riverside or Uber
          </motion.p>

          <div className="border-t border-yacht-charcoal/20">
            {/* 01 — Riverside Marina (GA) */}
            <motion.div
              id="general-admission"
              className="flex items-start gap-5 md:gap-8 py-8 md:py-10 border-b border-yacht-charcoal/20 scroll-mt-24"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-yacht-charcoal/35 text-sm md:text-base font-medium tabular-nums pt-2">
                01
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="text-3xl md:text-5xl font-bold text-yacht-charcoal tracking-tight leading-none">
                  Park at Riverside Marina
                </h2>
                <p className="text-yacht-charcoal/55 text-xs md:text-sm uppercase tracking-wider font-semibold mt-3">
                  General admission · where most people park
                </p>
                <p className="text-yacht-charcoal/75 text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
                  Friday <strong>$20</strong> · Saturday{" "}
                  <strong>FREE until 2 PM</strong>, then $20.{" "}
                  <strong>Cash only</strong>, pay when you arrive — no
                  reservation needed. A free shuttle (5–6 minutes) and ferry (8
                  minutes down the river) run to the festival doors all day,
                  both days.
                </p>
                <a
                  href={RIVERSIDE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-yacht-blue font-semibold text-sm md:text-base mt-4 hover:underline"
                >
                  11000 Freud St — open in Google Maps{" "}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </motion.div>

            {/* 02 — Rideshare */}
            <motion.div
              className="flex items-start gap-5 md:gap-8 py-8 md:py-10 border-b border-yacht-charcoal/20"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <span className="text-yacht-charcoal/35 text-sm md:text-base font-medium tabular-nums pt-2">
                02
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="text-3xl md:text-5xl font-bold text-yacht-charcoal tracking-tight leading-none">
                  Or Skip Parking — Take an Uber
                </h2>
                <p className="text-yacht-charcoal/75 text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
                  Honestly, we recommend it. Drop-off is right at the
                  Roostertail entrance at {ROOSTERTAIL_ADDRESS} — no lot, no
                  shuttle, no finding your car at midnight.
                </p>
              </div>
            </motion.div>

            {/* 03 — Roostertail lots, first come first served (online sales ended) */}
            <motion.div
              id="reserved-parking"
              className="flex items-start gap-5 md:gap-8 py-8 md:py-10 border-b border-yacht-charcoal/20 scroll-mt-24"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="text-yacht-charcoal/35 text-sm md:text-base font-medium tabular-nums pt-2">
                03
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="text-3xl md:text-5xl font-bold text-yacht-charcoal tracking-tight leading-none">
                  Roostertail Lots — First Come, First Served
                </h2>
                <p className="text-yacht-charcoal/55 text-xs md:text-sm uppercase tracking-wider font-semibold mt-3">
                  On the property · pay when you arrive · while spots last
                </p>
                <p className="text-yacht-charcoal/75 text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
                  The lots on the Roostertail property are first come, first
                  served — no reservations, just pull in and pay at the lot.{" "}
                  <strong>Premium (Main Lot) $100</strong> · Medium Lot $40 ·
                  Back Lot $30 per day. We can't guarantee you a spot, but there
                  should still be spots available both days — and if the lots
                  fill, Riverside Marina is minutes away with the free shuttle.
                  No in-and-out — once you're parked, you're parked for the day.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LOT MAP */}
      <section id="lot-map" className="pb-16 md:pb-24 px-6 md:px-8 scroll-mt-24">
        <div className="max-w-3xl mx-auto">
          <motion.p
            className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-6 md:mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Roostertail Lot Map
          </motion.p>
          <LotMap />
          <p className="text-yacht-charcoal/70 text-base md:text-lg text-center mt-8">
            Would you like to dock your boat here?{" "}
            <a
              href="/guide#bring-your-boat"
              className="text-yacht-blue font-semibold underline underline-offset-4 hover:text-yacht-blue/80"
            >
              Fill out this form
            </a>
            .
          </p>
        </div>
      </section>

      {/* QUICK FAQ */}
      <section className="pb-16 md:pb-24 px-6 md:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.p
            className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Quick Answers
          </motion.p>
          <motion.h2
            className="text-3xl md:text-5xl font-bold text-yacht-charcoal mb-8 md:mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Parking FAQ
          </motion.h2>

          <div className="bg-white rounded-2xl px-6 md:px-9 py-2 shadow-lg border border-yacht-charcoal/10">
            {PARKING_FAQ.map((item, i) => (
              <FaqRow
                key={i}
                q={item.q}
                a={item.a}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>

          <p className="text-yacht-charcoal/50 text-sm text-center mt-8">
            More questions?{" "}
            <a href="/faq" className="text-yacht-blue font-semibold hover:underline">
              See the full FAQ
            </a>{" "}
            or{" "}
            <a
              href="/contact"
              className="text-yacht-blue font-semibold hover:underline"
            >
              get in touch
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Parking;
